'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'

export function ComboDiegoContent() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  function handleBuy() {
    setShowCheckout(true)
    setCheckoutError('')
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Barra topo */}
      <div className="bg-violet-600 text-white text-center py-2 px-3 text-xs sm:text-sm font-medium">
        74 aulas. 2 cursos completos. Pagamento unico de R$ 297.
      </div>

      {/* Nav - Identidade Diego Abner */}
      <nav className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="text-lg font-bold text-white">Diego Abner</span>
          <div className="hidden sm:flex items-center gap-4 text-sm">
            <a href="#cursos" className="text-gray-400 hover:text-teal-400 transition">Cursos</a>
            <a href="#faq" className="text-gray-400 hover:text-violet-400 transition">FAQ</a>
            <a href="/login" className="text-gray-400 hover:text-white transition">Entrar</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/40 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 pt-10 pb-12 sm:pt-16 sm:pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-violet-500/15 border border-violet-500/25 text-violet-300 rounded-full text-[11px] sm:text-xs font-semibold mb-5 sm:mb-6 uppercase tracking-wide">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Combo exclusivo - 2 cursos pelo preco de 1
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
              De &quot;nunca programei&quot; a{' '}
              <span className="text-violet-400">sistemas em producao</span>{' '}
              usando IA
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto">
              74 aulas que te levam de zero absoluto a criar, deployar e vender software profissional. Sem precisar aprender a programar do jeito tradicional.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6 sm:mb-8">
              <button onClick={handleBuy}
                className="w-full sm:w-auto bg-violet-600 hover:bg-violet-500 text-white font-bold py-3.5 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-violet-600/30 cursor-pointer">
                GARANTIR COMBO POR R$ 297
              </button>
              <p className="text-xs sm:text-sm text-gray-400">Pagamento unico. Acesso vitalicio.</p>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">74 aulas</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">+19h de conteudo</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">3 cases reais</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">Audio profissional</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problema */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            O vibecoding promete. Mas 90% dos projetos morrem em 3 meses.
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">
            Porque pedir codigo para a IA sem entender o que esta pedindo e como dar uma planta de predio para alguem que nunca viu uma obra.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              {
                icon: '\u{1F534}',
                title: 'Copia tutorial do YouTube',
                desc: 'Funciona no video, quebra no seu projeto. Voce nao entende o porque e fica dependente de tutoriais pra sempre.',
              },
              {
                icon: '\u{1F534}',
                title: 'Pede codigo sem especificar',
                desc: 'A IA gera algo que "parece funcionar". Tres meses depois o sistema vira uma colcha de retalhos impossivel de manter.',
              },
              {
                icon: '\u{1F534}',
                title: 'Nao sabe deployar',
                desc: 'Criou o sistema mas esta preso no localhost. Nao sabe colocar no ar, configurar dominio ou manter rodando.',
              },
              {
                icon: '\u{1F534}',
                title: 'Nao tem processo',
                desc: 'Cada dia comeca do zero. Sem Git, sem backup, sem checklist. Perde trabalho, repete erros, nunca sente seguranca.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 sm:p-6">
                <div className="text-2xl mb-3">{item.icon}</div>
                <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solucao: Os 2 cursos */}
      <section id="cursos" className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            A solucao e simples: aprender na ordem certa.
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-10 sm:mb-14 max-w-2xl mx-auto">
            Primeiro voce entende a arquitetura. Depois voce constroi. Dois cursos que se encaixam como pecas de um quebra-cabeca.
          </p>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Pre-Vibecoding */}
            <div className="bg-gradient-to-b from-teal-950/50 to-gray-900/50 border border-teal-500/20 rounded-2xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/15 border border-teal-500/25 text-teal-400 rounded-full text-[11px] font-semibold mb-4 uppercase">
                Curso 1 - Fundacao
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">Pre-Vibecoding</h3>
              <p className="text-xs text-gray-500 mb-2">Instrutor: Diego Abner</p>
              <p className="text-sm text-teal-200/70 mb-5">De &quot;pedidor de codigo&quot; para &quot;arquiteto de sistemas&quot;</p>

              <div className="space-y-2.5 mb-6">
                {[
                  { mod: 'M0', title: 'Mentalidade de Arquiteto', aulas: 5 },
                  { mod: 'M1', title: 'Anatomia de um Sistema', aulas: 5 },
                  { mod: 'M2', title: 'A Arte da Especificacao', aulas: 5 },
                  { mod: 'M3', title: 'Versionamento Profissional', aulas: 5 },
                  { mod: 'M4', title: 'Qualidade e Testes', aulas: 5 },
                  { mod: 'M5', title: 'Projeto Final Guiado', aulas: 5 },
                ].map((m) => (
                  <div key={m.mod} className="flex items-center gap-3 text-sm">
                    <span className="text-teal-400 font-mono text-xs bg-teal-500/10 px-2 py-0.5 rounded shrink-0">{m.mod}</span>
                    <span className="text-gray-300">{m.title}</span>
                    <span className="text-gray-600 text-xs ml-auto shrink-0">{m.aulas} aulas</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-400 border-t border-gray-700/50 pt-4">
                <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                30 aulas - ~6h de conteudo
              </div>
            </div>

            {/* Vibecoding PRO */}
            <div className="bg-gradient-to-b from-violet-950/50 to-gray-900/50 border border-violet-500/20 rounded-2xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-500/15 border border-violet-500/25 text-violet-400 rounded-full text-[11px] font-semibold mb-4 uppercase">
                Curso 2 - Construcao
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">Vibecoding PRO</h3>
              <p className="text-xs text-gray-500 mb-2">Instrutor: Diego Abner</p>
              <p className="text-sm text-violet-200/70 mb-5">Do zero ao deploy. Sistemas reais em producao.</p>

              <div className="space-y-2.5 mb-6">
                {[
                  { mod: 'M0', title: 'Preparando a Maquina', aulas: 4 },
                  { mod: 'M1', title: 'O Vocabulario Essencial', aulas: 5 },
                  { mod: 'M2', title: 'Seu Primeiro Projeto', aulas: 6 },
                  { mod: 'M3', title: 'Backend com Supabase', aulas: 6 },
                  { mod: 'M4', title: 'Deploy em Producao', aulas: 6 },
                  { mod: 'M5', title: 'Funcionalidades Avancadas', aulas: 6 },
                  { mod: 'M6', title: 'Operacao e Manutencao', aulas: 4 },
                  { mod: 'M7', title: 'Replicando para Clientes', aulas: 4 },
                  { mod: 'B', title: 'Cases Reais Detalhados', aulas: 3 },
                ].map((m) => (
                  <div key={m.mod} className="flex items-center gap-3 text-sm">
                    <span className="text-violet-400 font-mono text-xs bg-violet-500/10 px-2 py-0.5 rounded shrink-0">{m.mod}</span>
                    <span className="text-gray-300">{m.title}</span>
                    <span className="text-gray-600 text-xs ml-auto shrink-0">{m.aulas} aulas</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-400 border-t border-gray-700/50 pt-4">
                <svg className="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                44 aulas - ~13h de conteudo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jornada visual */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Sua jornada em 4 fases
          </h2>

          <div className="space-y-4 sm:space-y-6">
            {[
              {
                phase: '01',
                title: 'Mentalidade',
                desc: 'Voce entende que vibecoding sem arquitetura e receita pra desastre. Aprende a pensar como arquiteto de software antes de escrever uma linha.',
                color: 'teal',
              },
              {
                phase: '02',
                title: 'Especificacao',
                desc: 'Aprende a criar documentos de especificacao que a IA entende. CLAUDE.md, fluxos de usuario, edge cases. A IA passa a entregar codigo que funciona de primeira.',
                color: 'teal',
              },
              {
                phase: '03',
                title: 'Construcao',
                desc: 'Cria seu primeiro projeto do zero com React, Supabase e Claude Code. Frontend, backend, autenticacao, pagamentos. Tudo funcionando.',
                color: 'violet',
              },
              {
                phase: '04',
                title: 'Producao',
                desc: 'Deploya no Vercel e VPS, configura dominio, monitora, faz backup. Seu sistema esta no ar, acessivel por qualquer pessoa no mundo.',
                color: 'violet',
              },
            ].map((item) => (
              <div key={item.phase} className="flex gap-4 sm:gap-6 items-start">
                <div className={`text-2xl sm:text-3xl font-black shrink-0 w-10 sm:w-14 ${item.color === 'teal' ? 'text-teal-500/30' : 'text-violet-500/30'}`}>
                  {item.phase}
                </div>
                <div className="pb-4 sm:pb-6 border-b border-gray-800">
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            3 sistemas reais construidos com vibecoding
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">
            Nao sao projetos de portfolio. Sao negocios em producao faturando com usuarios reais.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                name: 'Diego Abner',
                url: 'app.diegoabneroficial.com',
                desc: 'Plataforma de IA para negocios locais com cursos, fluxos interativos e pagamentos.',
                stack: 'Next.js, React, Supabase, Vercel',
              },
              {
                name: 'Negocios S/A',
                url: 'app.antoniofogaca.com.br',
                desc: 'Sistema de gestao operacional com area de membros, curso de 31 aulas e ferramentas.',
                stack: 'Vite, React, Express, VPS Hetzner',
              },
              {
                name: 'Pequeno Astronauta',
                url: 'game.ebuz.com.br',
                desc: 'Jogo pixel art espacial com 15 planetas, sistema de progressao e musica chiptune.',
                stack: 'Vanilla JS, Canvas, GitHub Pages',
              },
            ].map((c) => (
              <div key={c.name} className="bg-gray-800/40 border border-gray-700/40 rounded-xl p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-white mb-1">{c.name}</h3>
                <p className="text-violet-400 text-xs mb-3">{c.url}</p>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed mb-3">{c.desc}</p>
                <p className="text-[11px] text-gray-600 font-mono">{c.stack}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* O que inclui */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            O que voce recebe no combo
          </h2>

          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {[
              { icon: '\u{1F4DA}', text: '74 aulas em audio profissional (Google TTS Chirp3-HD)' },
              { icon: '\u{1F3AF}', text: '30 aulas de fundamentos (Pre-Vibecoding)' },
              { icon: '\u{1F680}', text: '44 aulas hands-on (Vibecoding PRO)' },
              { icon: '\u{1F4CB}', text: 'Templates de especificacao e CLAUDE.md' },
              { icon: '\u{1F4BB}', text: '3 cases reais detalhados passo a passo' },
              { icon: '\u{1F527}', text: 'Setup completo: Git, Supabase, Vercel, VPS' },
              { icon: '\u{1F4B0}', text: 'Modulo de precificacao e entrega para clientes' },
              { icon: '\u{267E}\u{FE0F}', text: 'Acesso vitalicio a ambos os cursos' },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start bg-gray-800/30 border border-gray-700/30 rounded-xl p-4">
                <span className="text-xl shrink-0">{item.icon}</span>
                <p className="text-sm text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-xl mx-auto">
          <div className="bg-gradient-to-b from-violet-950/60 to-gray-900/60 border border-violet-500/30 rounded-2xl p-6 sm:p-10 text-center">
            <div className="text-xs sm:text-sm text-violet-300 font-semibold uppercase tracking-wide mb-4">
              Combo Pre-Vibecoding + Vibecoding PRO
            </div>

            <div className="mb-2">
              <span className="text-gray-500 line-through text-lg sm:text-xl">De R$ 594</span>
            </div>

            <div className="mb-1">
              <span className="text-4xl sm:text-5xl md:text-6xl font-black text-white">R$ 297</span>
            </div>
            <p className="text-sm text-gray-400 mb-6 sm:mb-8">
              Pagamento unico. Sem mensalidade. Acesso pra sempre.
            </p>

            <button onClick={handleBuy}
              className="w-full bg-violet-600 hover:bg-violet-500 text-white font-bold py-4 px-8 rounded-xl text-lg transition shadow-lg shadow-violet-600/30 mb-4 cursor-pointer">
              QUERO O COMBO POR R$ 297
            </button>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-400">
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                Pagamento seguro
              </span>
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                PIX, cartao ou boleto
              </span>
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                7 dias de garantia
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Para quem e / nao e */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-green-500">{'\u2713'}</span> Para quem e
              </h3>
              <div className="space-y-3">
                {[
                  'Empreendedores que querem criar seus proprios sistemas sem depender de devs',
                  'Profissionais que ja tentaram vibecoding mas ficaram travados',
                  'Quem quer oferecer criacao de software como servico',
                  'Pessoas com ideias de SaaS/app mas sem background tecnico',
                  'Quem valoriza processo e quer construir certo desde o inicio',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    <p className="text-sm text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-red-500">{'\u2715'}</span> Para quem nao e
              </h3>
              <div className="space-y-3">
                {[
                  'Quem busca atalho magico sem querer entender o processo',
                  'Programadores seniores que ja dominam arquitetura de software',
                  'Quem espera aprender linguagens de programacao tradicionais',
                  'Pessoas que nao tem disponibilidade para praticar',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    <p className="text-sm text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Perguntas frequentes
          </h2>

          <div className="space-y-3">
            {[
              {
                q: 'Preciso saber programar?',
                a: 'Nao. O Pre-Vibecoding parte do zero absoluto e te ensina a pensar como arquiteto de software. O Vibecoding PRO usa IA (Claude Code) para escrever o codigo por voce. Voce precisa entender o que esta pedindo, nao como codificar.',
              },
              {
                q: 'Qual a diferenca entre o Pre e o PRO?',
                a: 'O Pre-Vibecoding ensina a mentalidade e o processo: como especificar, versionar, testar e manter software. O Vibecoding PRO e hands-on: voce constroi sistemas reais do zero ao deploy. Um e a fundacao, o outro e a construcao.',
              },
              {
                q: 'Posso comprar so um dos cursos?',
                a: 'Sim, cada curso e vendido separadamente por R$ 297. No combo voce leva os dois pelo preco de um. E a forma mais inteligente de comecar.',
              },
              {
                q: 'Quanto tempo tenho para fazer os cursos?',
                a: 'Acesso vitalicio. Faca no seu ritmo, revise quantas vezes quiser. As aulas sao em audio, entao voce pode ouvir no carro, na academia ou enquanto trabalha.',
              },
              {
                q: 'O que e vibecoding?',
                a: 'E a pratica de criar software usando IA como copiloto. Ao inves de escrever codigo linha por linha, voce descreve o que quer e a IA implementa. Mas sem processo e arquitetura, o resultado e fragil. Esses cursos resolvem exatamente isso.',
              },
              {
                q: 'Preciso de alguma ferramenta paga?',
                a: 'Para praticar o Vibecoding PRO, recomendamos Claude Code (~US$ 20/mes) e uma VPS Hetzner (~US$ 5/mes). Nao e obrigatorio para assistir, mas e o setup ideal para construir junto.',
              },
              {
                q: 'E se eu nao gostar?',
                a: 'Garantia incondicional de 7 dias. Pediu reembolso, devolvemos 100%. Sem burocracia.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex justify-between items-center p-4 sm:p-5 text-left cursor-pointer"
                >
                  <span className="text-sm sm:text-base font-medium text-white pr-4">{item.q}</span>
                  <svg className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {faqOpen === i && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            Voce pode continuar tentando sozinho. Ou pode comecar com o processo certo.
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mb-6 sm:mb-8">
            74 aulas. Do zero ao deploy. Acesso vitalicio. R$ 297.
          </p>

          <button onClick={handleBuy}
            className="bg-violet-600 hover:bg-violet-500 text-white font-bold py-3.5 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-violet-600/30 mb-4 cursor-pointer">
            GARANTIR MEU COMBO AGORA
          </button>
          <p className="text-xs text-gray-500">Pagamento seguro via Assas. PIX, cartao ou boleto.</p>
        </div>
      </section>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCheckout(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Finalizar compra</h3>
                <p className="text-sm text-gray-500">Combo Pre-Vibecoding + Vibecoding PRO</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {checkoutError}
                </div>
              )}
              <TransparentCheckout
                productType="course"
                productId="combo-vibecoding-pre-pro"
                amount={29700}
                title="Combo Pre-Vibecoding + Vibecoding PRO"
                description="2 cursos completos, 74 aulas, acesso vitalicio"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?payment=' + paymentId)
                }}
                onError={(error) => {
                  setCheckoutError(error)
                }}
                allowBoleto={true}
                maxInstallments={6}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer - Identidade Diego */}
      <footer className="py-6 sm:py-8 px-4 bg-gray-950 border-t border-gray-800 text-center">
        <p className="text-xs sm:text-sm text-gray-500">
          Combo Vibecoding - Pre-Vibecoding + Vibecoding PRO
        </p>
        <p className="text-xs text-gray-600 mt-1">Diego Abner LTDA - CNPJ 29.436.457/0001-25</p>
      </footer>
    </div>
  )
}
